'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { useLocale } from '@/lib/i18n'
import { localePath } from '@/lib/i18n/types'
import { useAuthModal } from '@/components/auth/auth-context'
import { Menu, X } from 'lucide-react'
import { AuthModal } from '@/components/auth/auth-modal'

interface StaticHeaderProps {
  alwaysScrolled?: boolean
}

export function StaticHeader({ alwaysScrolled = false }: StaticHeaderProps) {
  const [scrolled, setScrolled] = useState(alwaysScrolled)
  const [visible, setVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { t, locale } = useLocale()
  const { authModalOpen, setAuthModalOpen } = useAuthModal()
  // Locale-aware homepage links: '/' for en, '/cs' etc. for the rest
  const homeHref = localePath(locale)
  const pricingHref = `${homeHref}#pricing`

  useEffect(() => {
    if (alwaysScrolled) return

    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY < 80) {
        setVisible(true)
      } else if (currentScrollY < lastScrollY) {
        setVisible(true)
      } else {
        setVisible(false)
      }

      setScrolled(currentScrollY > 80)
      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY, alwaysScrolled])

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      visible ? 'translate-y-0' : '-translate-y-full'
    } ${
      scrolled
        ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm'
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 lg:px-6 h-16 flex items-center">
        <Link className="flex items-center justify-center" href={homeHref}>
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
            <img src="/chatbotty-logo.svg" className="h-7 w-7" alt="Chatbotty logo" />
          </div>
          <div>
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent">{t('homepage.brand_name')}</span>
            <p className={`text-sm transition-colors duration-300 ${scrolled ? 'text-gray-600' : 'text-white/60'}`}>{t('homepage.brand_subtitle')}</p>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="ml-auto hidden md:flex gap-6 items-center">
          <Link
            className={`text-sm font-medium transition-colors duration-300 ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/80 hover:text-white'}`}
            href={pricingHref}
          >
            {t('homepage.nav.pricing')}
          </Link>
          <Link
            className={`text-sm font-medium transition-colors duration-300 ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/80 hover:text-white'}`}
            href="/faq"
          >
            {t('homepage.nav.faq')}
          </Link>
          {/* TODO: Uncomment when blog articles are ready
          <Link
            className={`text-sm font-medium transition-colors duration-300 ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/80 hover:text-white'}`}
            href={`/blog/${locale}`}
          >
            {t('homepage.nav.blog')}
          </Link>
          */}
          <Link
            className={`text-sm font-medium transition-colors duration-300 ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/80 hover:text-white'}`}
            href={`/help/${locale}`}
          >
            {t('homepage.nav.help')}
          </Link>
          <Button
            size="sm"
            onClick={() => setAuthModalOpen(true)}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border-0 shadow-lg"
          >
            {t('homepage.nav.sign_in')}
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className={`ml-auto md:hidden p-2 transition-colors duration-300 ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/80 hover:text-white'}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden bg-white border-t border-gray-100 transition-all duration-300 overflow-hidden ${
        mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <nav className="container mx-auto px-4 py-4 space-y-4">
          <Link
            className="block text-lg font-medium text-gray-700 hover:text-indigo-600 transition-colors duration-200 py-2"
            href={pricingHref}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('homepage.nav.pricing')}
          </Link>
          <Link
            className="block text-lg font-medium text-gray-700 hover:text-indigo-600 transition-colors duration-200 py-2"
            href="/faq"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('homepage.nav.faq')}
          </Link>
          {/* TODO: Uncomment when blog articles are ready
          <Link
            className="block text-lg font-medium text-gray-700 hover:text-indigo-600 transition-colors duration-200 py-2"
            href={`/blog/${locale}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('homepage.nav.blog')}
          </Link>
          */}
          <Link
            className="flex items-center text-lg font-medium text-gray-700 hover:text-indigo-600 transition-colors duration-200 py-2"
            href={`/help/${locale}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('homepage.nav.help')}
          </Link>
          <Button
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border-0 shadow-lg"
            onClick={() => {
              setMobileMenuOpen(false)
              setAuthModalOpen(true)
            }}
          >
            {t('homepage.nav.sign_in')}
          </Button>
        </nav>
      </div>

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </header>
  )
}
